import React, { useEffect, useState } from 'react';

import { Bookmark as IconBookmark, BookmarkFilled } from '@digital-hig/icon';
import Button from '@digital-hig/mui/Button';
import IconButton from '@digital-hig/mui/IconButton';
import SvgIcon from '@digital-hig/mui/SvgIcon';
import { useTypedTranslation } from 'auonline2-next/lib/useTypedTranslation';

import twConfig from '../../../../tailwind.config';
import { useBookmarkController } from '../../../apis/collections/hooks';
import { getBookmarksCollectionId } from '../../../apis/collections/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipInfoContent,
  TooltipTrigger,
} from '../../../components/presentational/tooltip/Tooltip';
import useWindowSize from '../../../helpers/useWindowSize';

export interface BookmarkProps {
  baseURL?: string;
  contentID: string;
  contentTitle: string;
  variant: 'text' | 'icon';
  mode?: 'light' | 'dark';
}

export default function Bookmark(props: BookmarkProps): JSX.Element {
  const { t } = useTypedTranslation(['common']);
  const {
    baseURL,
    contentTitle,
    contentID,
    variant = 'icon',
    mode = 'dark',
  } = props;

  const [error, setError] = useState('');
  const { width } = useWindowSize();
  const lgWidth = parseInt(twConfig.theme.extend.screens.md, 10);
  const isMobile = width && width < lgWidth;
  const id = getBookmarksCollectionId();

  const { isBookmarked, isLoading, isError, reset, toggleBookmark } =
    useBookmarkController({
      baseURL,
      id,
      contentTitle,
      contentID,
    });

  const errorCloseHandler = () => {
    setError('');
    reset();
  };

  const clickHandler = async () => {
    try {
      await toggleBookmark();
    } catch (e) {
      setError(t('bookmarks.error', { ns: 'common' }));
    }
  };

  useEffect(() => {
    if (isError && !error) {
      setError(t('bookmarks.error', { ns: 'common' }));
    }
  }, [isError]);
  const buttonColor = mode === 'dark' ? 'white' : 'black';
  return (
    <Tooltip open={!!error} onOpenChange={errorCloseHandler}>
      <TooltipTrigger asChild>
        {isMobile || variant === 'icon' ? (
          <IconButton
            disabled={isLoading}
            variant="text"
            onClick={clickHandler}
          >
            <SvgIcon fontSize="medium">
              {isBookmarked ? (
                <BookmarkFilled color={buttonColor} />
              ) : (
                <IconBookmark color={buttonColor} />
              )}
            </SvgIcon>
          </IconButton>
        ) : (
          <Button
            variant="text"
            disabled={isLoading}
            onClick={clickHandler}
            className="au-text-white"
            startIcon={
              <SvgIcon fontSize="medium">
                {isBookmarked ? (
                  <BookmarkFilled color={buttonColor} />
                ) : (
                  <IconBookmark color={buttonColor} />
                )}
              </SvgIcon>
            }
            style={{ color: 'white' }}
          >
            {t('Bookmark', { ns: 'common' })}
          </Button>
        )}
      </TooltipTrigger>
      <TooltipContent>
        <TooltipInfoContent>{error}</TooltipInfoContent>
      </TooltipContent>
    </Tooltip>
  );
}

Bookmark.defaultProps = {};

Bookmark.propTypes = {};

Bookmark.attributes = {
  baseURL: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com/',
  },
  topicId: {
    type: 'string',
    default: '',
  },
  theme: {
    type: 'string',
    default: 'horizontal',
  },
  className: {
    type: 'string',
    default: '',
  },
};
